// ===========================================
// Admin Settings React Query Hooks
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as adminSettingsApi from '../api/adminSettings.api';
import { apiErrorMessage } from '../utils/apiError';
import { bookingKeys } from './useBooking';
import { invoiceKeys } from './useInvoices';
import { LATE_FEE_PREVIEW_PREFIX } from './useLateFeePreview';
import { invalidateEnquiryConfig } from './useEnquiries';
import type { AvatarSettings } from '../types/avatar.types';
import type {
  InvoiceSettingsInput,
  ReminderStepCreateInput,
  ReminderStepUpdateInput,
  ColorPaletteInput,
  AddColorInput,
  AcademicYearInput,
  AdminNavigationInput,
  CalendarStatusColorsInput,
  RoleColorsInput,
  SchoolInfoInput,
  LegalSettingsInput,
  PaymentSettingsInput,
  ExtraLessonSettingsInput,
  ReportsSettingsInput,
  ReportScheduleInput,
  ReportScheduleUpdateInput,
} from '../types/adminSettings.types';
// OnboardingConfigInput type used by adminSettingsApi.updateOnboardingConfig internally

// ===========================================
// QUERY KEYS
// ===========================================

export const adminSettingsKeys = {
  all: ['admin-settings'] as const,
  allSettings: () => [...adminSettingsKeys.all, 'all'] as const,
  invoice: () => [...adminSettingsKeys.all, 'invoice'] as const,
  reminderSteps: () => [...adminSettingsKeys.all, 'invoice', 'reminder-steps'] as const,
  colors: () => [...adminSettingsKeys.all, 'colors'] as const,
  academicYear: () => [...adminSettingsKeys.all, 'academic-year'] as const,
  navigation: () => [...adminSettingsKeys.all, 'navigation'] as const,
  calendarColors: () => [...adminSettingsKeys.all, 'calendar-colors'] as const,
  roleColors: () => [...adminSettingsKeys.all, 'role-colors'] as const,
  schoolInfo: () => [...adminSettingsKeys.all, 'school-info'] as const,
  avatar: () => [...adminSettingsKeys.all, 'avatar'] as const,
  legal: () => [...adminSettingsKeys.all, 'legal'] as const,
  payment: () => [...adminSettingsKeys.all, 'payment'] as const,
  paymentRates: () => [...adminSettingsKeys.all, 'payment-rates'] as const,
  extraLesson: () => [...adminSettingsKeys.all, 'extra-lesson'] as const,
  onboarding: () => [...adminSettingsKeys.all, 'onboarding'] as const,
  reports: () => [...adminSettingsKeys.all, 'reports'] as const,
  reportSchedules: () => [...adminSettingsKeys.all, 'report-schedules'] as const,
  digestRecipients: () => [...adminSettingsKeys.all, 'digest-recipients'] as const,
};

// ===========================================
// ALL SETTINGS
// ===========================================

/**
 * Get all admin settings at once
 */
export function useAllAdminSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.allSettings(),
    queryFn: adminSettingsApi.getAllSettings,
  });
}

/**
 * Reset all settings to defaults
 */
export function useResetAllSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetAllSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.all });
      enqueueSnackbar('All settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset settings', { variant: 'error' });
    },
  });
}

// ===========================================
// INVOICE SETTINGS
// ===========================================

/**
 * Get invoice settings
 */
export function useInvoiceSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.invoice(),
    queryFn: adminSettingsApi.getInvoiceSettings,
  });
}

/**
 * Update invoice settings
 */
export function useUpdateInvoiceSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: InvoiceSettingsInput) => adminSettingsApi.updateInvoiceSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: LATE_FEE_PREVIEW_PREFIX });
      enqueueSnackbar('Invoice settings updated', { variant: 'success' });
    },
    // The server's message (e.g. "The final notice must come after the overdue reminder." or a
    // zod field error), never axios's "Request failed with status code 400" (invoice reminders
    // /qa Phase 6 MEDIUM-1: with ~18 fields the admin could not tell which one was refused).
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to update invoice settings'), { variant: 'error' });
    },
  });
}

/**
 * Reset invoice settings to defaults
 */
export function useResetInvoiceSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetInvoiceSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: LATE_FEE_PREVIEW_PREFIX });
      enqueueSnackbar('Invoice settings reset to defaults', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to reset invoice settings'), { variant: 'error' });
    },
  });
}

// ===========================================
// INVOICE REMINDER STEPS (#372 slice B)
// ===========================================

/** The ladder, in send order, plus whether late fees pin the Last Chance step. */
export function useReminderSteps() {
  return useQuery({
    queryKey: adminSettingsKeys.reminderSteps(),
    queryFn: adminSettingsApi.getReminderSteps,
  });
}

/** Step writes: the server's own message on every refusal (STEP_LIMIT, STEPS_TOO_CLOSE, ...). */
function useStepMutationHandlers(successMessage: string, failureFallback: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reminderSteps() });
      // The card's lastChanceWarning rides on the invoice settings; the invoice preview follows the ladder.
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
      queryClient.invalidateQueries({ queryKey: LATE_FEE_PREVIEW_PREFIX });
      // A step's enabled switch is the same boolean System Emails shows.
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      // Every invoice's reminder history names the steps (by prefix: all invoices refetch).
      queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'reminders'] });
      enqueueSnackbar(successMessage, { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, failureFallback), { variant: 'error' });
    },
  };
}

export function useCreateReminderStep() {
  return useMutation({
    mutationFn: (input: ReminderStepCreateInput) => adminSettingsApi.createReminderStep(input),
    ...useStepMutationHandlers('Reminder step added', 'Failed to add the reminder step'),
  });
}

export function useUpdateReminderStep() {
  return useMutation({
    mutationFn: ({ stepId, input }: { stepId: string; input: ReminderStepUpdateInput }) =>
      adminSettingsApi.updateReminderStep(stepId, input),
    ...useStepMutationHandlers('Reminder step updated', 'Failed to update the reminder step'),
  });
}

export function useDeleteReminderStep() {
  return useMutation({
    mutationFn: (stepId: string) => adminSettingsApi.deleteReminderStep(stepId),
    ...useStepMutationHandlers('Reminder step removed', 'Failed to remove the reminder step'),
  });
}

// ===========================================
// COLOR PALETTE
// ===========================================

interface UseColorPaletteOptions {
  /** Whether to enable the query. Default: true */
  enabled?: boolean;
}

/**
 * Get color palette
 * @param options - Query options including enabled flag
 */
export function useColorPalette(options: UseColorPaletteOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: adminSettingsKeys.colors(),
    // A role without `system_settings.read` gets a 403 here (the palette lives under
    // /admin/settings). That refusal is final, so it resolves to "no school palette" and the
    // pickers show the brand presets at once, instead of retrying for ~7 s (roles slice 1a
    // step 4 /qa L1). Every other error still throws and keeps the client's retry policy.
    queryFn: async () => {
      try {
        return await adminSettingsApi.getColorPalette();
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 403) return null;
        throw error;
      }
    },
    enabled,
  });
}

/**
 * Update entire color palette
 */
export function useUpdateColorPalette() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ColorPaletteInput) => adminSettingsApi.updateColorPalette(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.colors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Color palette updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update color palette', { variant: 'error' });
    },
  });
}

/**
 * Add a single custom color
 */
export function useAddColor() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (color: AddColorInput) => adminSettingsApi.addCustomColor(color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.colors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Color added to palette', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to add color', { variant: 'error' });
    },
  });
}

/**
 * Remove a color from the palette
 */
export function useRemoveColor() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (colorValue: string) => adminSettingsApi.removeColor(colorValue),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.colors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Color removed from palette', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to remove color', { variant: 'error' });
    },
  });
}

/**
 * Reset color palette to brand defaults
 */
export function useResetColorPalette() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetColorPalette,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.colors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Color palette reset to brand defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset color palette', { variant: 'error' });
    },
  });
}

// ===========================================
// ACADEMIC YEAR
// ===========================================

/**
 * Get academic year settings
 */
export function useAcademicYear() {
  return useQuery({
    queryKey: adminSettingsKeys.academicYear(),
    queryFn: adminSettingsApi.getAcademicYear,
  });
}

/**
 * Update academic year settings
 */
export function useUpdateAcademicYear() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: AcademicYearInput) => adminSettingsApi.updateAcademicYear(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.academicYear() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Academic year settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update academic year settings', { variant: 'error' });
    },
  });
}

// ===========================================
// ADMIN NAVIGATION
// ===========================================

/**
 * Get admin navigation settings (the route needs system_settings.read; AdminLayout gates it, #272)
 */
export function useAdminNavigation(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminSettingsKeys.navigation(),
    queryFn: adminSettingsApi.getAdminNavigation,
    enabled: options.enabled ?? true,
  });
}

/**
 * Update admin navigation settings
 */
export function useUpdateAdminNavigation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: AdminNavigationInput) => adminSettingsApi.updateAdminNavigation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.navigation() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Navigation settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update navigation settings', { variant: 'error' });
    },
  });
}

/**
 * Reset admin navigation to defaults
 */
export function useResetAdminNavigation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetAdminNavigation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.navigation() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Navigation reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset navigation', { variant: 'error' });
    },
  });
}

// ===========================================
// CALENDAR STATUS COLORS
// ===========================================

/**
 * Get calendar status colors
 */
export function useCalendarStatusColors() {
  return useQuery({
    queryKey: adminSettingsKeys.calendarColors(),
    queryFn: adminSettingsApi.getCalendarStatusColors,
  });
}

/**
 * Update calendar status colors
 */
export function useUpdateCalendarStatusColors() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CalendarStatusColorsInput) => adminSettingsApi.updateCalendarStatusColors(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.calendarColors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Calendar colors updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update calendar colors', { variant: 'error' });
    },
  });
}

/**
 * Reset calendar status colors to defaults
 */
export function useResetCalendarStatusColors() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetCalendarStatusColors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.calendarColors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Calendar colors reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset calendar colors', { variant: 'error' });
    },
  });
}

// ===========================================
// ROLE COLORS
// ===========================================

/**
 * Get role color settings
 */
export function useRoleColors() {
  return useQuery({
    queryKey: adminSettingsKeys.roleColors(),
    queryFn: adminSettingsApi.getRoleColors,
  });
}

/**
 * Update role color settings
 */
export function useUpdateRoleColors() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: RoleColorsInput) => adminSettingsApi.updateRoleColors(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.roleColors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Role colors updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update role colors', { variant: 'error' });
    },
  });
}

/**
 * Reset role colors to defaults
 */
export function useResetRoleColors() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetRoleColors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.roleColors() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Role colors reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset role colors', { variant: 'error' });
    },
  });
}

// ===========================================
// SCHOOL INFO
// ===========================================

/**
 * Get school info settings
 */
export function useSchoolInfo() {
  return useQuery({
    queryKey: adminSettingsKeys.schoolInfo(),
    queryFn: adminSettingsApi.getSchoolInfo,
  });
}

/**
 * Update school info settings
 */
export function useUpdateSchoolInfo() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: SchoolInfoInput) => adminSettingsApi.updateSchoolInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.schoolInfo() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      // Office days and the zone feed the enquiry pipeline's due days and "today".
      invalidateEnquiryConfig(queryClient);
      enqueueSnackbar('School info updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update school info', { variant: 'error' });
    },
  });
}

/**
 * Reset school info to defaults
 */
export function useResetSchoolInfo() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetSchoolInfo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.schoolInfo() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      // The reset rewrites the zone and office days the enquiry pipeline reads.
      invalidateEnquiryConfig(queryClient);
      enqueueSnackbar('School info reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset school info', { variant: 'error' });
    },
  });
}

// ===========================================
// LEGAL SETTINGS
// ===========================================

/**
 * Get legal settings
 */
export function useLegalSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.legal(),
    queryFn: adminSettingsApi.getLegalSettings,
  });
}

/**
 * Update legal settings
 */
export function useUpdateLegalSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: LegalSettingsInput) => adminSettingsApi.updateLegalSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.legal() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Legal settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update legal settings', { variant: 'error' });
    },
  });
}

/**
 * Reset legal settings to defaults
 */
export function useResetLegalSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetLegalSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.legal() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Legal settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset legal settings', { variant: 'error' });
    },
  });
}

// ===========================================
// AVATAR SETTINGS (Priority 11)
// ===========================================

/**
 * Get avatar settings
 */
export function useAdminAvatarSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.avatar(),
    queryFn: adminSettingsApi.getAvatarSettings,
  });
}

/**
 * Update avatar settings
 */
export function useUpdateAvatarSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: Partial<AvatarSettings>) => adminSettingsApi.updateAvatarSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.avatar() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Avatar settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update avatar settings', { variant: 'error' });
    },
  });
}

/**
 * Reset avatar settings to defaults
 */
export function useResetAvatarSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetAvatarSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.avatar() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Avatar settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset avatar settings', { variant: 'error' });
    },
  });
}

// ===========================================
// PAYMENT SETTINGS
// ===========================================

/**
 * Get available Stripe processing rates by country
 */
export function usePaymentRates() {
  return useQuery({
    queryKey: adminSettingsKeys.paymentRates(),
    queryFn: adminSettingsApi.getPaymentRates,
    staleTime: Infinity, // Rates rarely change, cache indefinitely
  });
}

/**
 * Get payment settings
 */
export function usePaymentSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.payment(),
    queryFn: adminSettingsApi.getPaymentSettings,
  });
}

/**
 * Update payment settings
 */
export function useUpdatePaymentSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: PaymentSettingsInput) => adminSettingsApi.updatePaymentSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.payment() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Payment settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update payment settings', { variant: 'error' });
    },
  });
}

/**
 * Reset payment settings to defaults
 */
export function useResetPaymentSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetPaymentSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.payment() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      enqueueSnackbar('Payment settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset payment settings', { variant: 'error' });
    },
  });
}

// ===========================================
// EXTRA LESSON SETTINGS
// ===========================================

/**
 * Get extra lesson settings
 */
export function useExtraLessonSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.extraLesson(),
    queryFn: adminSettingsApi.getExtraLessonSettings,
  });
}

/**
 * Update extra lesson settings
 */
export function useUpdateExtraLessonSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ExtraLessonSettingsInput) => adminSettingsApi.updateExtraLessonSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.extraLesson() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.settings() });
      enqueueSnackbar('Extra lesson settings updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update settings', { variant: 'error' });
    },
  });
}

/**
 * Reset extra lesson settings to defaults
 */
export function useResetExtraLessonSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetExtraLessonSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.extraLesson() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.settings() });
      enqueueSnackbar('Extra lesson settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset settings', { variant: 'error' });
    },
  });
}

// ===========================================
// REPORT SETTINGS (2026-09-16)
// ===========================================

/**
 * Which report cards show on the Reports Overview page.
 *
 * ⚠️ The Overview page gates each card's data fetch on this, so callers there
 * must distinguish "still loading the settings" from "the admin hid everything".
 * Rendering an empty Overview while this query is in flight would flash a false
 * empty state.
 */
export function useReportsSettings() {
  return useQuery({
    queryKey: adminSettingsKeys.reports(),
    queryFn: adminSettingsApi.getReportsSettings,
  });
}

/**
 * Replace the visible-card selection.
 *
 * Invalidates the reports-overview queries as well as the settings themselves:
 * un-hiding a card must make it fetch, and the Overview's `enabled` flags are
 * derived from this data.
 */
export function useUpdateReportsSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ReportsSettingsInput) => adminSettingsApi.updateReportsSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reports() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: ['reports', 'overview'] });
      enqueueSnackbar('Report settings updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update report settings', { variant: 'error' });
    },
  });
}

/** Restore all nine cards. */
export function useResetReportsSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetReportsSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reports() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      queryClient.invalidateQueries({ queryKey: ['reports', 'overview'] });
      enqueueSnackbar('Report settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset report settings', { variant: 'error' });
    },
  });
}

// ===========================================
// REPORT SCHEDULES (Slice 2, 2026-09-16)
// ===========================================

export function useReportSchedules() {
  return useQuery({
    queryKey: adminSettingsKeys.reportSchedules(),
    queryFn: adminSettingsApi.listReportSchedules,
  });
}

/** Admins who may be added to a schedule. */
export function useDigestRecipients() {
  return useQuery({
    queryKey: adminSettingsKeys.digestRecipients(),
    queryFn: adminSettingsApi.listDigestRecipients,
  });
}

export function useCreateReportSchedule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ReportScheduleInput) => adminSettingsApi.createReportSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reportSchedules() });
      enqueueSnackbar('Report schedule created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create the schedule', { variant: 'error' });
    },
  });
}

export function useUpdateReportSchedule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReportScheduleUpdateInput }) =>
      adminSettingsApi.updateReportSchedule(id, data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reportSchedules() });
      // A stored recipient who can no longer receive reports is taken off by the save and
      // named, never silently (roles slice 2 step 6).
      const removed = schedule.removedRecipients ?? [];
      if (removed.length > 0) {
        const names = removed.map((r) => `${r.firstName} ${r.lastName}`.trim()).join(', ');
        enqueueSnackbar(`Report schedule updated. Removed ${names}: they can no longer receive reports.`, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Report schedule updated', { variant: 'success' });
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update the schedule', { variant: 'error' });
    },
  });
}

export function useDeleteReportSchedule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminSettingsApi.deleteReportSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reportSchedules() });
      enqueueSnackbar('Report schedule deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to delete the schedule', { variant: 'error' });
    },
  });
}

/**
 * Send this digest to yourself.
 *
 * ⚠️ Does NOT invalidate the schedule list: a test send deliberately bypasses
 * the ledger and never stamps `lastSentAt`, so nothing about the schedule has
 * changed. Refetching would imply otherwise.
 */
export function useSendReportScheduleTest() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminSettingsApi.sendReportScheduleTest(id),
    onSuccess: (data) => {
      enqueueSnackbar(`Test digest sent to ${data.sentTo}`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to send the test digest', { variant: 'error' });
    },
  });
}

// ===========================================
// ONBOARDING CONFIG
// ===========================================

/**
 * Get onboarding config for the school (the route needs system_settings.read, #272)
 */
export function useOnboardingConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminSettingsKeys.onboarding(),
    queryFn: adminSettingsApi.getOnboardingConfig,
    enabled: options.enabled ?? true,
  });
}

/**
 * Update onboarding config
 */
export function useUpdateOnboardingConfig() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.updateOnboardingConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.onboarding() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      // "Meet & Greet required" switches the two MNG stages on and off.
      invalidateEnquiryConfig(queryClient);
      enqueueSnackbar('Onboarding configuration updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update onboarding config', {
        variant: 'error',
      });
    },
  });
}

/**
 * Reset onboarding config to defaults
 */
export function useResetOnboardingConfig() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: adminSettingsApi.resetOnboardingConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.onboarding() });
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.allSettings() });
      invalidateEnquiryConfig(queryClient);
      enqueueSnackbar('Onboarding configuration reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset onboarding config', {
        variant: 'error',
      });
    },
  });
}
